import { useEffect, useMemo, useRef, useState } from 'react';

import { Tabs } from '@components';
import Icon from '@components/icon/Icon';
import RadioGroup from '@components/radio-button/RadioGroup';
import {
  Event,
  MixpanelEventV2,
  sendMixpanelEvent,
  sendMixpanelEventV2
} from '@core/analytics/mixpanel';
import ModalV2 from '@layouts/modal/Modal.v2';
import { FormDataType } from '@types';
import { TabPill } from 'types/tabs';

import {
  REVIEW_MEMBERS_TABS,
  TAB_DESCRIPTIONS,
  TAB_EMPTY_MESSAGES
} from './constants';
import GroupChangesCard from './GroupChangesCard';
import { hasFrequencyChanged } from './helper';
import IneligibleMemberTable from './IneligibleMemberTable';
import MemberTable from './MemberTable';

import { PaymentCollectionType } from '../../constants';
import { CustomMemberUpdate, UpdateEligibleMembersResponse } from '../../types';

type ReviewMembersModalProps = {
  data: UpdateEligibleMembersResponse;
  formData: FormDataType;
  initialFormData: FormDataType;
  onCancel: () => void;
  onConfirm: (customMemberUpdates: CustomMemberUpdate[]) => void;
  isLoading?: boolean;
  isPaymentLinkSentForGroup?: boolean;
};

const getPeriodLabel = (paymentCollectionType: string): string => {
  if (paymentCollectionType === PaymentCollectionType.MONTHLY) return 'month';
  if (paymentCollectionType === PaymentCollectionType.WEEKLY) return 'week';
  return '';
};

const getGroupChanges = (
  current: FormDataType,
  initial: FormDataType
): string[] => {
  const changes: string[] = [];
  if (current.amount !== initial.amount) changes.push('amount');
  if (hasFrequencyChanged(current, initial)) changes.push('frequency/billing');
  if (current.startDate !== initial.startDate) changes.push('start date');
  if (current.endDate !== initial.endDate) changes.push('end date');
  return changes;
};

const getDueDateOptions = (period: string) => [
  { key: 'current', value: `Current ${period}`.trim() },
  { key: 'next', value: `Next ${period}`.trim() }
];

const ReviewMembersModal = ({
  data,
  formData,
  initialFormData,
  onCancel,
  onConfirm,
  isLoading = false,
  isPaymentLinkSentForGroup = false
}: ReviewMembersModalProps) => {
  const hasTrackedView = useRef(false);

  const periodLabel = getPeriodLabel(formData.paymentCollectionType);
  const customMembers = useMemo(
    () => data.eligible_members.filter((m) => !m.is_default_member),
    [data.eligible_members]
  );
  const defaultMembers = useMemo(
    () => data.eligible_members.filter((m) => m.is_default_member),
    [data.eligible_members]
  );
  const { ineligible_members: ineligibleMembers } = data;

  const [selectedCustomIds, setSelectedCustomIds] = useState<Set<number>>(
    () => new Set<number>()
  );
  const [selectedDefaultIds, setSelectedDefaultIds] = useState<Set<number>>(
    () => new Set(defaultMembers.map((m) => m.group_member_id))
  );
  const [dueDateConsent, setDueDateConsent] = useState<string>('next');

  const tabs: TabPill[] = useMemo(
    () => [
      {
        id: REVIEW_MEMBERS_TABS.CUSTOM,
        label: 'Custom members',
        count: customMembers.length
      },
      {
        id: REVIEW_MEMBERS_TABS.DEFAULT,
        label: 'Default members',
        count: defaultMembers.length
      },
      {
        id: REVIEW_MEMBERS_TABS.INELIGIBLE,
        label: 'Ineligible members',
        count: ineligibleMembers.length
      }
    ],
    [customMembers, defaultMembers, ineligibleMembers]
  );

  const getDefaultTab = () => {
    if (customMembers.length > 0) return tabs[0];
    if (defaultMembers.length > 0) return tabs[1];
    return tabs[2];
  };
  const [selectedTab, setSelectedTab] = useState<TabPill>(getDefaultTab);

  const toggleMember = (
    groupMemberId: number,
    setIds: React.Dispatch<React.SetStateAction<Set<number>>>
  ) => {
    setIds((prev) => {
      const next = new Set(prev);
      if (next.has(groupMemberId)) {
        next.delete(groupMemberId);
      } else {
        next.add(groupMemberId);
      }
      return next;
    });
  };

  const togglePageMembers = (
    addIds: number[],
    removeIds: number[],
    setIds: React.Dispatch<React.SetStateAction<Set<number>>>
  ) => {
    setIds((prev) => {
      const next = new Set(prev);
      addIds.forEach((id) => next.add(id));
      removeIds.forEach((id) => next.delete(id));
      return next;
    });
  };

  const totalSelectedCount = selectedCustomIds.size + selectedDefaultIds.size;

  const isOnceGroup =
    formData.paymentCollectionType === PaymentCollectionType.ONCE;

  const hasMemberPaymentLinkSent = data.eligible_members.some(
    (m) =>
      m.is_payment_link_sent &&
      (selectedCustomIds.has(m.group_member_id) ||
        selectedDefaultIds.has(m.group_member_id))
  );

  const hasSelectedCustomMembersWithPaymentLinkSent =
    selectedCustomIds.size > 0 &&
    data.eligible_members.some(
      (m) => selectedCustomIds.has(m.group_member_id) && m.is_payment_link_sent
    );
  const hasSelectedDefaultMembersWithPaymentLinkSent =
    selectedTab.id === REVIEW_MEMBERS_TABS.DEFAULT &&
    selectedDefaultIds.size > 0 &&
    data.eligible_members.some(
      (m) => selectedDefaultIds.has(m.group_member_id) && m.is_payment_link_sent
    );

  const hasSelectedMembersWithPaymentLinkSent =
    selectedTab.id === REVIEW_MEMBERS_TABS.CUSTOM
      ? hasSelectedCustomMembersWithPaymentLinkSent
      : hasSelectedDefaultMembersWithPaymentLinkSent;

  const hasDueDateChange = hasFrequencyChanged(formData, initialFormData);

  const showPaymentLinkConsent =
    hasDueDateChange &&
    (isPaymentLinkSentForGroup || hasMemberPaymentLinkSent) &&
    hasSelectedMembersWithPaymentLinkSent &&
    ((formData.paymentCollectionType === PaymentCollectionType.MONTHLY &&
      Number(formData.intervalFrequency) === 1) ||
      formData.paymentCollectionType === PaymentCollectionType.WEEKLY) &&
    selectedTab.id !== REVIEW_MEMBERS_TABS.INELIGIBLE;

  const getReviewAnalyticsParams = (
    updatedCustomCount: number,
    updatedDefaultCount: number
  ) => ({
    group_changes: getGroupChanges(formData, initialFormData),
    custom_members: customMembers.length,
    default_members: defaultMembers.length,
    ineligible_members: ineligibleMembers.length,
    updated_custom_members: updatedCustomCount,
    updated_default_members: updatedDefaultCount,
    this_next_month: showPaymentLinkConsent
      ? `${dueDateConsent === 'current' ? 'This' : 'Next'} ${periodLabel}`
      : 'N/A'
  });

  useEffect(() => {
    if (hasTrackedView.current) return;
    hasTrackedView.current = true;
    const reviewParams = getReviewAnalyticsParams(0, defaultMembers.length);
    sendMixpanelEvent(Event.GROUP_EDIT_REVIEW_VIEWED, reviewParams);
    sendMixpanelEventV2(
      MixpanelEventV2.GROUP_EDIT_REVIEW_MODAL_VIEWED,
      reviewParams
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleConfirm = () => {
    const allSelectedIds = new Set([
      ...selectedCustomIds,
      ...selectedDefaultIds
    ]);
    const isSkipInterval = hasDueDateChange && dueDateConsent === 'next';

    const customMemberUpdates: CustomMemberUpdate[] = data.eligible_members
      .filter((m) => allSelectedIds.has(m.group_member_id))
      .map((m) => ({
        member_id: m.member_id,
        skip_current_interval:
          isSkipInterval &&
          m.is_payment_link_sent &&
          ((formData.paymentCollectionType === PaymentCollectionType.MONTHLY &&
            Number(formData.intervalFrequency) === 1) ||
            formData.paymentCollectionType === PaymentCollectionType.WEEKLY),
        default_member: m.is_default_member
      }));

    sendMixpanelEvent(
      Event.MEMBER_DETAIL_UPDATION_VIA_GROUP_EDIT,
      getReviewAnalyticsParams(selectedCustomIds.size, selectedDefaultIds.size)
    );

    onConfirm(customMemberUpdates);
  };

  const renderEmptyState = (tabId: string) => (
    <p className="py-8 text-center text-sm text-cofeeNeutral7">
      {TAB_EMPTY_MESSAGES[tabId]}
    </p>
  );

  return (
    <ModalV2
      title="Review changes and apply to members"
      onClose={onCancel}
      className="h-200 w-175 min-w-165"
      bodyClassName="!overflow-hidden"
      primaryButtonLabel="Apply"
      primaryButtonClick={handleConfirm}
      primaryButtonLoading={isLoading}
      secondaryButtonLabel="Cancel"
      secondaryButtonClick={onCancel}
      infoText={
        <span className="text-sm font-medium text-cofeeNeutral7">
          {String(totalSelectedCount).padStart(2, '0')} Selected
        </span>
      }
      primaryButtonStyle="!min-w-30"
      secondaryButtonStyle="!min-w-30"
    >
      <div className="flex h-full flex-col">
        <div className="min-h-0 flex-1 overflow-y-auto">
          <GroupChangesCard
            formData={formData}
            initialFormData={initialFormData}
          />

          <p className="text-base font-semibold text-cofeeNeutral10">
            Choose which members{' '}
            {isOnceGroup ? 'the amount change' : 'these changes'} should apply
            to
          </p>

          <Tabs
            tabs={tabs}
            selectedTab={selectedTab}
            onTabSelect={(tab) => setSelectedTab(tab)}
            className="mb-4"
          />

          {selectedTab.id === REVIEW_MEMBERS_TABS.CUSTOM &&
            (customMembers.length > 0 ? (
              <MemberTable
                members={customMembers}
                selectedIds={selectedCustomIds}
                onToggle={(id) => toggleMember(id, setSelectedCustomIds)}
                onTogglePageAll={(add, remove) =>
                  togglePageMembers(add, remove, setSelectedCustomIds)
                }
                description={TAB_DESCRIPTIONS[
                  REVIEW_MEMBERS_TABS.CUSTOM
                ].replace('settings', isOnceGroup ? 'amount' : 'settings')}
                periodLabel={periodLabel}
                showConsent={showPaymentLinkConsent}
              />
            ) : (
              renderEmptyState(REVIEW_MEMBERS_TABS.CUSTOM)
            ))}

          {selectedTab.id === REVIEW_MEMBERS_TABS.DEFAULT &&
            (defaultMembers.length > 0 ? (
              <MemberTable
                members={defaultMembers}
                selectedIds={selectedDefaultIds}
                onToggle={(id) => toggleMember(id, setSelectedDefaultIds)}
                onTogglePageAll={(add, remove) =>
                  togglePageMembers(add, remove, setSelectedDefaultIds)
                }
                description={TAB_DESCRIPTIONS[REVIEW_MEMBERS_TABS.DEFAULT]}
                periodLabel={periodLabel}
                showConsent={showPaymentLinkConsent}
              />
            ) : (
              renderEmptyState(REVIEW_MEMBERS_TABS.DEFAULT)
            ))}

          {selectedTab.id === REVIEW_MEMBERS_TABS.INELIGIBLE &&
            (ineligibleMembers.length > 0 ? (
              <IneligibleMemberTable
                members={ineligibleMembers}
                description={TAB_DESCRIPTIONS[REVIEW_MEMBERS_TABS.INELIGIBLE]}
              />
            ) : (
              renderEmptyState(REVIEW_MEMBERS_TABS.INELIGIBLE)
            ))}
        </div>

        {/* Only for monthly and weekly groups */}
        {showPaymentLinkConsent && (
          <div className="mt-4 shrink-0 rounded-lg border border-cofeeGreyBlue100 bg-cofeeGreyBlue50 p-4">
            <div className="mb-3 flex items-start gap-2">
              <Icon name="info-orange" size="medium" />
              <p className="text-base text-cofeeGreyBlue600">
                {`The payment link for the current ${periodLabel} has already been sent to some members. Select 'Next ${periodLabel}' to prevent any duplication of payment.`.replace(
                  /\s{2,}/g,
                  ' '
                )}
              </p>
            </div>
            <RadioGroup
              options={getDueDateOptions(periodLabel)}
              selectedValue={dueDateConsent}
              handleChange={setDueDateConsent}
              className="ml-7"
              iconStyle="!size-5"
              labelStyle="!text-base"
            />
          </div>
        )}
      </div>
    </ModalV2>
  );
};

export default ReviewMembersModal;
